'use client';

import { App, ConfigProvider } from 'antd';
import { usePathname } from 'next/navigation';
import { type ReactNode, Suspense, useMemo } from 'react';
import { ToastContainer } from 'react-toastify';

import AuthSessionSync from '~/shared/components/auth-session-sync/auth-session-sync';
import DisableMobileZoom from '~/shared/components/disable-mobile-zoom';
import GlobalErrorBoundary from '~/shared/components/global-error-boundary';
import { MediaContextProvider } from '~/shared/components/media-query';
import RouterProgress from '~/shared/components/router-progress';
import StyledComponentsRegistry from '~/shared/components/styled-components-registry';
import { settings } from '~/shared/configs/config';
import { RootContext } from '~/shared/configs/root-context';
import { checkIsAdminPanel, checkIsPlatform } from '~/shared/helpers/check-helpers';
import { ApolloWrapper } from '~/shared/libs/apollo/apollo-provider';
import { useAppSelector } from '~/shared/store/hooks';
import { getAppUser } from '~/shared/store/slices/app/selectors';
import { getShowHeader } from '~/shared/store/slices/lessons/selectors';
import type { Locale } from '~/universal/constants';

type ClientProvidersProps = {
  lng: Locale;
  children: ReactNode;
};

const AppClientProviders = ({ lng, children }: ClientProvidersProps) => {
  const pathname = usePathname();
  const userFromStore = useAppSelector(getAppUser);
  const showHeader = useAppSelector(getShowHeader);
  const isAdminPanel = checkIsAdminPanel(pathname);
  const isPlatform = checkIsPlatform(pathname);
  const rootContext = useMemo(
    () => ({
      lng,
      user: userFromStore,
      showHeader,
      isPlatform,
      isAdminPanel,
    }),
    [lng, userFromStore, showHeader, isPlatform, isAdminPanel],
  );

  return (
    <GlobalErrorBoundary lng={lng}>
      <StyledComponentsRegistry>
        <ConfigProvider theme={settings.theme}>
          <App>
            <ApolloWrapper>
              <Suspense fallback={null}>
                <RouterProgress delay={80} />
              </Suspense>
              <AuthSessionSync lng={lng} userId={userFromStore?._id} />
              <DisableMobileZoom />
              <ToastContainer style={{ zIndex: 99999 }} />
              <RootContext.Provider value={rootContext}>{children}</RootContext.Provider>
            </ApolloWrapper>
          </App>
        </ConfigProvider>
      </StyledComponentsRegistry>
    </GlobalErrorBoundary>
  );
};

export default AppClientProviders;
